import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { whatsappCloudService, WhatsAppCloudTemplate } from '@/services/integrations/whatsappCloudService';
import { TEMPLATE_STATUS_BADGE } from '@/utils/whatsappTemplate';
import TemplateVariableForm, { TemplateSendPayload } from './TemplateVariableForm';

interface TemplatePickerProps {
  onClose: () => void;
  onSend: (payload: TemplateSendPayload) => Promise<void>;
  /** Optional header text override — e.g. "Start a conversation with Rajiv"
   * for the new-conversation flow, instead of the default "Send a template". */
  title?: string;
  /** Optional banner rendered under the header, e.g. a failed-start error —
   * onSend intentionally never throws (matches the composer's convention of
   * showing errors via local state rather than exceptions), so a caller that
   * needs to surface one renders it here instead. */
  banner?: React.ReactNode;
}

type CategoryFilter = 'ALL' | 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

/**
 * Approved-template picker for the composer (FE-14) — a persistent list +
 * live preview split view (not the settings page's TemplateGrid): agents
 * comparing a few candidate templates by name need the body snippet to
 * recognize which one they mean, and shouldn't lose their place doing it.
 */
const TemplatePicker: React.FC<TemplatePickerProps> = ({ onClose, onSend, title, banner }) => {
  const [templates, setTemplates] = useState<WhatsAppCloudTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('ALL');
  const [selected, setSelected] = useState<WhatsAppCloudTemplate | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    whatsappCloudService.getTemplates()
      // manual_send=false: the system generates part of it at send time (e.g. the
      // Razorpay link in crm_payment_request) -- an agent picking it by hand cannot.
      .then((all) => setTemplates(all.filter((t) => t.status === 'APPROVED' && t.manual_send !== false)))
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => ({
    MARKETING: templates.filter((t) => t.category === 'MARKETING').length,
    UTILITY: templates.filter((t) => t.category === 'UTILITY').length,
    AUTHENTICATION: templates.filter((t) => t.category === 'AUTHENTICATION').length,
  }), [templates]);

  const filtered = useMemo(() => {
    let list = templates;
    if (category !== 'ALL') list = list.filter((t) => t.category === category);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((t) => t.element_name.toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q));
    return list;
  }, [templates, category, search]);

  const handleSend = async (payload: TemplateSendPayload) => {
    setSending(true);
    try {
      await onSend(payload);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#1B2531]/40 flex justify-end z-30">
      <div className="w-full max-w-3xl bg-white h-full flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[15.5px] font-bold">{title || 'Send a template'}</div>
            <div className="text-xs text-gray-500 mt-0.5">{templates.length} approved</div>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50">
            <X className="w-[15px] h-[15px]" />
          </button>
        </div>

        {banner}

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 h-[34px] border border-gray-200 rounded-lg px-2.5 bg-gray-50 mb-2.5">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or body text…"
              className="flex-1 text-[12.5px] bg-transparent border-0 outline-none placeholder:text-gray-400"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION'] as CategoryFilter[]).map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${
                  category === c ? 'bg-[#153d23] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {c === 'ALL' ? 'All' : c.toLowerCase()}
                {c !== 'ALL' && ` ${counts[c]}`}
              </button>
            ))}
          </div>
        </div>

        {/* Persistent split view — the list stays visible while the preview
         * on the right updates live, so comparing a few candidates never
         * loses your place the way a grid-then-drilldown swap would. */}
        <div className="flex-1 flex min-h-0">
          <div className="w-[280px] flex-shrink-0 border-r border-gray-100 overflow-y-auto">
            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brandColor mx-auto" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 px-4 text-sm text-gray-400">No templates match.</div>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelected(t)}
                  className={`w-full text-left px-3.5 py-2.5 border-b border-gray-50 ${
                    selected?.id === t.id
                      ? 'bg-[#eef5f0] shadow-[inset_3px_0_0_#153d23]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-1.5 mb-1">
                    <span className="text-[12.5px] font-bold font-mono truncate">{t.element_name}</span>
                    <span className={`flex-shrink-0 px-1.5 py-0.5 text-[9.5px] font-bold rounded-full ${
                      TEMPLATE_STATUS_BADGE[t.status] || 'bg-gray-100 text-gray-600'
                    }`}>
                      {t.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 line-clamp-2 leading-snug">{t.body}</p>
                  <div className="text-[10.5px] text-gray-400 mt-1">{t.category} · {t.language}</div>
                </button>
              ))
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col bg-gray-50">
            {selected ? (
              <TemplateVariableForm
                key={selected.id}
                template={selected}
                onCancel={onClose}
                onSend={handleSend}
                sending={sending}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400 px-8 text-center">
                Select a template on the left to preview it and fill in any variables.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;
